package com.arena_web.ui.sections.home

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.arena_web.ui.components.overlay.SupportPopup
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.sections.header.components.BrandLogo
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.AppTypography
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Img
import org.w3c.dom.Element
import org.w3c.dom.HTMLElement
import org.w3c.dom.asList
import org.w3c.dom.events.Event
import org.w3c.dom.events.MouseEvent

private const val URBANIST = AppTypography.PRIMARY_FONT_NAME
private const val URBANIST_FB = AppTypography.PRIMARY_FONT_FALLBACK

@Composable
fun HomeShowcase() {
    HomeAmbientOrbs()
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .sMaxWidth(1840)
            // Lift the page content above the fixed orb layer (z-index 0).
            .styleModifier {
                property("position", "relative")
                property("z-index", "1")
            },
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        // Top action bar (store CTAs left, hero tagline right)
        TopActionBar()

        Spacer(70)
        SectionCreateEvents()
        Spacer(80)
        SectionDiscoverTeammates()
        Spacer(80)
        SectionShareMoments()
        Spacer(80)
        SectionTrackStats()
        Spacer(100)
        GradientDivider()
        Spacer(100)
        Footer()
    }
}

// ============================================================================
// Top action bar — store buttons left, sign-in right
// ============================================================================

@Composable
private fun TopActionBar() {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .sPaddingTop(40)
            .styleModifier { property("position", "relative") }
    ) {
        // Top-left: stacked store buttons, pulled to the left edge of the showcase column.
        Box(
            modifier = Modifier.styleModifier {
                property("position", "absolute")
                property("left", s(-30))
                property("top", s(40))
            }
        ) {
            Column(modifier = Modifier.sGap(12)) {
                AppleStoreButton()
                GooglePlayButton()
            }
        }
        // Centered stage — big logo intro/fly-away first, then the animated tagline.
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .styleModifier {
                    property("display", "flex")
                    property("justify-content", "center")
                    property("position", "relative")
                }
        ) {
            HeroTagline()
            HeroIntroLogo()
        }
    }
}

// ============================================================================
// Hero tagline — Figma 9663:169326 (575×140 multi-line phrase with pill badges).
// Each chunk fades + slides in with a staggered delay so the line types itself.
// ============================================================================

val HeroChunkKeyframes = Keyframes {
    from { Modifier.translateY(10.px).opacity(0) }
    to { Modifier.translateY(0.px).opacity(1) }
}

// Each new word in the rotating sport pill rises in from below.
val HeroWordSwapKeyframes = Keyframes {
    from { Modifier.opacity(0).styleModifier { property("transform", "translateY(60%)") } }
    to { Modifier.opacity(1).styleModifier { property("transform", "translateY(0)") } }
}

// While the ring assembles segment by segment, the whole mark
// grows from roughly half size to full — mirroring the Figma storyboard (9655:148664)
// where each assembly stage is also larger than the previous one.
val HeroLogoIntroKeyframes = Keyframes {
    from { Modifier.styleModifier { property("transform", "scale(0.55)") } }
    to { Modifier.styleModifier { property("transform", "scale(1)") } }
}

// Ring flash expanding outward as the logo snaps to full size. Border only —
// box-shadows on this ring bled over the logo and read as a stray glow.
val HeroBurstKeyframes = Keyframes {
    from { Modifier.opacity(0).styleModifier { property("transform", "translate(-50%, -50%) scale(0.35)") } }
    each(12.percent) { Modifier.opacity(0.75) }
    to { Modifier.opacity(0).styleModifier { property("transform", "translate(-50%, -50%) scale(2.0)") } }
}

private const val HERO_TAGLINE_START_MS = HeroIntroTiming.TOTAL_MS - 200 // start fading words in just before logo lands
private const val HERO_LOGO_ID = "hero-intro-logo"
private const val HERO_GHOST_ID = "hero-intro-logo-ghost"

@Composable
private fun HeroIntroLogo() {
    // Outer wrapper only centers the stage. It must stay free of animated transforms,
    // otherwise keyframes would override the translateX(-50%) centering.
    Box(
        modifier = Modifier.styleModifier {
            property("position", "absolute")
            property("left", "50%")
            property("top", s(20))
            property("transform", "translateX(-50%)")
            property("pointer-events", "none")
            property("z-index", "5")
        }
    ) {
        // Burst ring — fires just before the assembly finishes and dissolves outward.
        Box(
            modifier = Modifier
                .sWidth(260)
                .sHeight(260)
                .styleModifier {
                    property("position", "absolute")
                    property("left", "50%")
                    property("top", "50%")
                    property("border-radius", "50%")
                    property("border", "2px solid rgba(0, 97, 255, 0.5)")
                }
                .animation(
                    HeroBurstKeyframes.toAnimation(
                        duration = 850.ms,
                        delay = (HeroIntroTiming.INTRO_MS - 280).ms,
                        timingFunction = AnimationTimingFunction.EaseOut,
                        fillMode = AnimationFillMode.Both,
                    )
                )
        )
        // The logo itself. The SVG assembles arc by arc (Figma storyboard 9655:148664)
        // while the container scales up; the fly-to-header leg is applied from HeroLogoFlight
        // because the landing spot must be measured at runtime.
        Box(
            modifier = Modifier
                .id(HERO_LOGO_ID)
                .animation(
                    HeroLogoIntroKeyframes.toAnimation(
                        duration = HeroIntroTiming.INTRO_MS.ms,
                        timingFunction = AnimationTimingFunction.EaseOut,
                        fillMode = AnimationFillMode.Both,
                    )
                )
        ) {
            // The official logo SVG (Figma 10124:146712) carries a baked-in blue halo
            // per segment (drop-shadow filters) — that glow is part of the brand mark,
            // not an effect added here. It's fetched and injected as inline markup so
            // each arc group can pop in on its own clock — an <img> can't do that.
            Div(
                attrs = {
                    style {
                        property("width", s(220))
                        property("height", s(152))
                        property("display", "block")
                    }
                    attr("role", "img")
                    attr("aria-label", "Arena logo")
                    ref { el ->
                        injectHeroSegmentStyles()
                        window.fetch("/icons/ic_proplay_logo_glow.svg")
                            .then { it.text() }
                            .then { svgText ->
                                el.innerHTML = svgText.unsafeCast<String>()
                                // Document order is bottom, left, right, top-left, top-right,
                                // inner; the storyboard assembles counterclockwise from the
                                // top-left arc with the inner arc landing last.
                                val assemblyOrder = listOf(3, 2, 4, 1, 5, 6)
                                el.querySelectorAll("g[filter]").asList()
                                    .filterIsInstance<Element>()
                                    .forEachIndexed { i, g ->
                                        g.setAttribute(
                                            "class",
                                            "hero-seg hero-seg-${assemblyOrder.getOrElse(i) { 6 }}"
                                        )
                                    }
                            }
                        onDispose { }
                    }
                }
            )
        }
    }
    HeroLogoFlight()
}

// Each arc sweeps into place along the ring's own orbit: it rotates around the ring
// center (transform-box: view-box) from a few degrees ahead while fading in, so the
// segments always sit perfectly on their circle — per-segment scaling made them look
// crooked mid-assembly. Injected as a raw stylesheet because Silk keyframes can't
// target the paths inside innerHTML-injected SVG.
private fun injectHeroSegmentStyles() {
    val styleId = "hero-logo-segment-styles"
    if (document.getElementById(styleId) != null) return
    val stagger = 100
    val style = document.createElement("style")
    style.id = styleId
    style.textContent = buildString {
        appendLine(
            """
            @keyframes hero-seg-sweep {
                from { opacity: 0; transform: rotate(18deg); }
                to { opacity: 1; transform: rotate(0deg); }
            }
            #$HERO_LOGO_ID .hero-seg {
                opacity: 0;
                transform-box: view-box;
                transform-origin: 50% 50%;
                animation: hero-seg-sweep 320ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
            }
            """.trimIndent()
        )
        repeat(6) { i ->
            appendLine("#$HERO_LOGO_ID .hero-seg-${i + 1} { animation-delay: ${40 + i * stagger}ms; }")
        }
    }
    document.head?.appendChild(style)
}

// FLIP hand-off: measure where the header's brand glyph actually sits and fly the hero
// logo precisely onto it, so the header glyph can take over in place without a visible seam.
// The flight is performed by a fixed-position clone parented to <body>: the original sits
// inside the page's stacking context and would slide UNDER the header mid-flight. A clone
// (rather than reparenting the original) keeps Compose's DOM management undisturbed.
@Composable
private fun HeroLogoFlight() {
    DisposableEffect(Unit) {
        val launch: () -> Unit = launch@{
            val logo = document.getElementById(HERO_LOGO_ID) as? HTMLElement ?: return@launch
            val from = logo.getBoundingClientRect()
            // BrandLogo renders in several headers; only the visible glyph has a real rect.
            val glyph = document.querySelectorAll("[data-brand-glyph]").asList()
                .filterIsInstance<HTMLElement>()
                .firstOrNull { it.getBoundingClientRect().width > 0.0 }
            val ghost = logo.cloneNode(true) as HTMLElement
            // The segment-pop CSS is scoped to #hero-intro-logo, so under the new id the
            // cloned SVG renders fully assembled — exactly the state the flight needs.
            ghost.id = HERO_GHOST_ID
            ghost.style.animation = "none"
            ghost.style.position = "fixed"
            ghost.style.left = "${from.left}px"
            ghost.style.top = "${from.top}px"
            ghost.style.width = "${from.width}px"
            ghost.style.height = "${from.height}px"
            ghost.style.margin = "0"
            ghost.style.zIndex = "10000"
            ghost.style.setProperty("pointer-events", "none")
            ghost.style.transform = "none"
            ghost.style.transformOrigin = "top left"
            document.body?.appendChild(ghost)
            logo.style.visibility = "hidden"
            // Reading the rect flushes the ghost's starting styles, arming the transition.
            ghost.getBoundingClientRect()
            // Slight negative-start bezier pulls the logo back before launching it — anticipation.
            ghost.style.transition =
                "transform ${HeroIntroTiming.FLY_MS}ms cubic-bezier(0.6, -0.15, 0.25, 1), " +
                    "opacity ${HeroIntroTiming.FLY_MS}ms ease-in"
            if (glyph != null) {
                val to = glyph.getBoundingClientRect()
                val scale = to.width / from.width
                ghost.style.transform =
                    "translate(${to.left - from.left}px, ${to.top - from.top}px) scale($scale)"
            } else {
                // No visible header glyph — fall back to flying off toward the corner and fading.
                ghost.style.transform = "translate(-45vw, -22vh) scale(0.2)"
                ghost.style.opacity = "0"
            }
        }
        val removeGhost = {
            document.getElementById(HERO_GHOST_ID)?.let { it.parentElement?.removeChild(it) }
        }
        val settle: () -> Unit = settle@{
            // Running animations keep every arc promoted to its own GPU layer, whose
            // upscaled rasterization leaves faint blurry blue edges ("glow"). Clearing
            // the animations once assembly is done re-renders the logo as crisp vector.
            val logo = document.getElementById(HERO_LOGO_ID) as? HTMLElement ?: return@settle
            // SVG groups are SVGElement, not HTMLElement, in Kotlin/JS — filter on Element
            // and write the style attribute directly or the segments are silently skipped.
            logo.querySelectorAll(".hero-seg").asList()
                .filterIsInstance<Element>()
                .forEach {
                    it.setAttribute("style", "animation: none; opacity: 1; transform: none")
                }
            logo.style.animation = "none"
        }
        val settleTimer = window.setTimeout(settle, HeroIntroTiming.INTRO_MS + 100)
        val flyTimer = window.setTimeout(launch, HeroIntroTiming.INTRO_MS + HeroIntroTiming.HOLD_MS)
        val swapTimer = window.setTimeout({
            // The header glyph has docked in this exact spot — retire the flying copy.
            removeGhost()
        }, HeroIntroTiming.TOTAL_MS + 80)
        onDispose {
            window.clearTimeout(settleTimer)
            window.clearTimeout(flyTimer)
            window.clearTimeout(swapTimer)
            removeGhost()
        }
    }
}

@Composable
private fun HeroTagline() {
    val t = LocalAppStrings.current
    // The tagline is the page's H1 — the only place the primary keywords
    // appear in a heading. display:contents keeps the h1 out of layout so
    // the absolutely-positioned chunks are untouched.
    H1(attrs = { style { property("display", "contents") } }) {
    // Outer container — 575×140 in Figma coordinates. Every chunk is absolutely positioned
    // at its exact Figma x/y so pills float at different heights, mixing with the big text.
    Box(
        modifier = Modifier
            .sWidth(575)
            .sHeight(140)
            .styleModifier { property("position", "relative") }
    ) {
        // "Arena Pro Play" — top-left, big Bold blue. zIndex 0 so any pills overlap on top.
        HeroChunk(left = 0, top = 8, delayMs = HERO_TAGLINE_START_MS, zIndex = 0) {
            HeroBigText(t.homeHeroBrand, color = "#0061FF", bold = true, sizePx = 46)
        }
        // "unites" — to the right of Arena Pro Play. zIndex 0 so the "athletes" pill overlaps on top.
        HeroChunk(left = 328, top = 8, delayMs = HERO_TAGLINE_START_MS + 300, zIndex = 0) {
            HeroBigText(t.homeHeroUnites, color = "#BED6F5", bold = false, sizePx = 46)
        }
        // "athletes" pill — Figma y=19 (sits next to "unites", slightly above its baseline).
        HeroChunk(left = 446, top = 19, delayMs = HERO_TAGLINE_START_MS + 600) { HeroPill(t.homeHeroAthletes) }

        // Second line content. Figma row 2: x=35, y=62, h=67.
        // "and teams" pill — nudged left from Figma's x=37.
        HeroChunk(left = 15, top = 75, delayMs = HERO_TAGLINE_START_MS + 900) { HeroPill(t.homeHeroAndTeams) }
        // "worldwide for" big text — original Figma x=145; pushed under the pills via z-index.
        HeroChunk(left = 145, top = 67, delayMs = HERO_TAGLINE_START_MS + 1200, zIndex = 0) {
            HeroBigText(t.homeHeroWorldwideFor, color = "#BED6F5", bold = false, sizePx = 36)
        }
        // "every game" pill — Figma y=96, dropped below "worldwide for" right edge.
        // After settling it starts cycling through actual sports — the product pitch in one pill.
        HeroChunk(left = 312, top = 96, delayMs = HERO_TAGLINE_START_MS + 1500) { HeroRotatingSportPill() }
    }
    }
}

internal val HeroSportWords =
    listOf("every game", "football", "basketball", "tennis", "volleyball", "padel")

@Composable
private fun HeroRotatingSportPill() {
    var rotations by remember { mutableStateOf(0) }
    LaunchedEffect(Unit) {
        // The pill only becomes visible at HERO_TAGLINE_START_MS + 1500 (+500ms fade),
        // so the countdown to the first swap starts from there: a full 3s of readable
        // "every game" before the sports start cycling.
        kotlinx.coroutines.delay((HERO_TAGLINE_START_MS + 1500 + 500 + 3000).toLong())
        while (true) {
            rotations++
            kotlinx.coroutines.delay(2200L)
        }
    }
    // Remounting on every rotation replays the word's rise-in animation; the very first
    // mount (rotations == 0) must stay still — HeroChunk already animates its arrival.
    key(rotations) {
        HeroPill(
            HeroSportWords[rotations % HeroSportWords.size],
            textModifier = if (rotations == 0) Modifier else Modifier.animation(
                HeroWordSwapKeyframes.toAnimation(
                    duration = 350.ms,
                    timingFunction = AnimationTimingFunction.EaseOut,
                    fillMode = AnimationFillMode.Both,
                )
            ),
        )
    }
}

@Composable
private fun HeroChunk(
    left: Int,
    top: Int,
    delayMs: Int,
    zIndex: Int = 2,
    content: @Composable () -> Unit,
) {
    Box(
        modifier = Modifier
            .zIndex(zIndex)
            .styleModifier {
                property("position", "absolute")
                property("left", s(left))
                property("top", s(top))
            }
            .animation(
                HeroChunkKeyframes.toAnimation(
                    duration = 500.ms,
                    delay = delayMs.ms,
                    timingFunction = AnimationTimingFunction.EaseOut,
                    fillMode = AnimationFillMode.Both,
                )
            )
    ) { content() }
}

@Composable
private fun HeroBigText(text: String, color: String, bold: Boolean, sizePx: Int) {
    SpanText(
        text,
        modifier = Modifier
            .sFontSize(sizePx)
            .sLineHeight(46)
            .sLetterSpacing(sizePx * 0.02)
            .fontWeight(if (bold) AppTypography.Bold else AppTypography.Medium)
            .fontFamily(URBANIST, URBANIST_FB)
            .styleModifier {
                property("color", color)
                property("white-space", "nowrap")
            }
    )
}

@Composable
private fun HeroPill(text: String, textModifier: Modifier = Modifier) {
    // 32-tall pill — Figma 9663:169329/169331/169339:
    // - background: linear-gradient(~6deg, rgba(0,59,174,0.08) 27%, rgba(3,87,230,0.08) 67%)
    // - border: 0.3px solid rgba(255,255,255,0.4) → gradient ring via mask-composite (project rule).
    // - backdrop-blur: 2px.
    Box(
        modifier = Modifier
            .sHeight(32)
            .sBorderRadius(100)
            .sPaddingLeft(22)
            .sPaddingRight(22)
            .styleModifier {
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("position", "relative")
                // Solid dark base hides any text underneath; Figma's faint gradient layers on top.
                property(
                    "background",
                    "${AppGradients.InteractiveSecondary}, rgba(8, 12, 28, 0.55)"
                )
                property("backdrop-filter", "blur(6px)")
                property("-webkit-backdrop-filter", "blur(6px)")
            }
    ) {
        SpanText(
            text,
            modifier = Modifier
                .sFontSize(18)
                .sLineHeight(22)
                .sLetterSpacing(0.36)
                .fontWeight(AppTypography.Medium)
                .fontFamily(URBANIST, URBANIST_FB)
                .styleModifier {
                    property("color", "#BED6F5")
                    property("white-space", "nowrap")
                }
                .then(textModifier)
        )
        // Gradient border ring — mask-composite trick (project rule on gradient borders).
        Box(
            modifier = Modifier
                .fillMaxSize()
                .styleModifier {
                    property("position", "absolute")
                    property("inset", "0")
                    property("border", "0.3px solid transparent")
                    property("border-radius", "100px")
                    property("background", "${AppGradients.ButtonBorderGradientPrimary} border-box")
                    property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                    property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                    property("-webkit-mask-composite", "xor")
                    property("mask-composite", "exclude")
                    property("pointer-events", "none")
                }
        )
    }
}

// ============================================================================
// Section 1 — Create Events
// ============================================================================

@Composable
private fun SectionCreateEvents() {
    val t = LocalAppStrings.current
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.Top,
        horizontalArrangement = Arrangement.SpaceBetween,
    ) {
        // LEFT — Events screen, pulled further left to spread the section out.
        Box(modifier = Modifier.sMarginLeft(-30)) {
            MockupCard(
                image = "/images/home/events.png",
                alt = t.homeAltEventsScreen,
                widthPx = 660,
                modifier = Modifier.revealOnScroll(0, eager = true),
            )
        }

        // CENTER — title + underline + 2 subtitles, then Create Event modal below
        Column(
            modifier = Modifier
                .sWidth(496)
                .flexShrink(0)
                .revealOnScroll(150, eager = true),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            SpanText(
                t.homeSectionCreateEventsTitle,
                modifier = Modifier
                    .sFontSize(32)
                    .fontWeight(AppTypography.SemiBold)
                    .sLineHeight(18)
                    .sLetterSpacing(0.64)
                    .color(AppColors.Text.Secondary)
                    .fontFamily(URBANIST, URBANIST_FB)
                    .styleModifier { property("text-align", "center") }
            )
            Box(modifier = Modifier.sHeight(8))
            // Underline sits under the word "Events" — Figma 9654:135445
            // (x=147 inside 288-wide title container, which itself starts at column-x=104).
            Box(modifier = Modifier.fillMaxWidth().sPaddingLeft(251)) {
                Box(
                    modifier = Modifier
                        .sWidth(102)
                        .sHeight(1)
                        .backgroundColor(AppColors.Text.Secondary)
                )
            }
            Box(modifier = Modifier.sHeight(4))
            Column(
                modifier = Modifier.sGap(2),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                SubtitleLine(t.homeSectionCreateEventsSubtitle1)
                SubtitleLine(t.homeSectionCreateEventsSubtitle2)
            }
            Box(modifier = Modifier.sHeight(40))
            // Per Figma 9654:135439: wrapper spans the full CENTER column (496px).
            // Modal (create-event.png) is centred inside (≈360px), arrows sit in the
            // empty band on each side — top-left ↖ on the LEFT outside the modal,
            // bottom-right ↗ on the RIGHT outside the modal.
            Box(
                modifier = Modifier.sWidth(496).flexShrink(0)
                    .styleModifier { property("position", "relative") }
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.Center,
                ) {
                    MockupCard(
                        image = "/images/home/create-event.png",
                        alt = t.homeAltCreateEventModal,
                        widthPx = 360,
                    )
                }
                // ↖ — left side, slightly above the vertical middle of the modal
                Box(
                    modifier = Modifier.styleModifier {
                        property("position", "absolute")
                        property("left", s(-15))
                        property("top", s(10))
                        property("z-index", "2")
                    }
                ) { DiagonalArrowPill(sizePx = 63, rotateDeg = -45, modifier = Modifier.revealOnScroll(500, pop = true, eager = true)) }
                // ↗ — right side, near the bottom of the modal
                Box(
                    modifier = Modifier.styleModifier {
                        property("position", "absolute")
                        property("right", s(-15))
                        property("bottom", s(0))
                        property("z-index", "2")
                    }
                ) { DiagonalArrowPill(sizePx = 63, rotateDeg = 45, modifier = Modifier.revealOnScroll(650, pop = true, eager = true), pulseDelayMs = 400) }
            }
        }

        // RIGHT — event-detail screen, pulled further right to spread the section out.
        Box(modifier = Modifier.sMarginRight(-30)) {
            MockupCard(
                image = "/images/home/event-detail.png",
                alt = t.homeAltEventDetailScreen,
                widthPx = 667,
                modifier = Modifier.revealOnScroll(300, eager = true),
            )
        }
    }
}

@Composable
private fun SubtitleLine(text: String) {
    SpanText(
        text,
        modifier = Modifier
            .sFontSize(18)
            .fontWeight(AppTypography.Regular)
            .sLineHeight(22)
            .sLetterSpacing(0.36)
            .color(AppColors.Text.Secondary)
            .fontFamily(URBANIST, URBANIST_FB)
            .styleModifier { property("text-align", "center") }
    )
}

// ============================================================================
// Section 2 — Discover Perfect Teammates
// ============================================================================

@Composable
private fun SectionDiscoverTeammates() {
    val t = LocalAppStrings.current
    Column(
        modifier = Modifier.fillMaxWidth().sGap(40),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        // Headline — underline sits only under the word "Teammates" (Figma 9680:174351,
        // Line 143 at x=958 / width=178, y=40 inside the 40-tall title row → flush below text).
        Column(
            modifier = Modifier.fillMaxWidth().sGap(2).revealOnScroll(0, eager = true),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            SpanText(
                t.homeSectionTeammatesTitle,
                modifier = Modifier
                    .sFontSize(32)
                    .fontWeight(AppTypography.SemiBold)
                    .sLineHeight(38)
                    .sLetterSpacing(0.64)
                    .color(AppColors.Text.Secondary)
                    .fontFamily(URBANIST, URBANIST_FB)
                    .styleModifier { property("text-align", "center") }
            )
            Box(modifier = Modifier.fillMaxWidth().sPaddingLeft(958)) {
                Box(
                    modifier = Modifier
                        .sWidth(178)
                        .sHeight(1)
                        .backgroundColor(AppColors.Text.Secondary)
                )
            }
        }
        Row(
            modifier = Modifier.fillMaxWidth().sGap(20),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.Center,
        ) {
            MockupCard("/images/home/discover-card.png", t.homeAltPlayerDiscoveryCard, widthPx = 460, modifier = Modifier.revealOnScroll(100, eager = true))
            // Right-pointing pill — Figma "Map Expand Icon" 9680:174451 (77px circle, arrow-up rotated 90°).
            DiagonalArrowPill(sizePx = 77, rotateDeg = 90, modifier = Modifier.revealOnScroll(600, pop = true, eager = true))
            MockupCard("/images/home/matched-interests.png", t.homeAltMatchedInterestsModal, widthPx = 600, modifier = Modifier.revealOnScroll(250, eager = true))
            // Right-pointing pill — Figma "Map Expand Icon" 9680:174600.
            DiagonalArrowPill(sizePx = 77, rotateDeg = 90, modifier = Modifier.revealOnScroll(700, pop = true, eager = true), pulseDelayMs = 400)
            // Right cluster — match-popup mockup + caption "It's your match!" (Figma 9680:174347).
            Column(
                modifier = Modifier.sGap(16).flexShrink(0).revealOnScroll(400, eager = true),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                MockupCard("/images/home/match-popup.png", t.homeAltItsAMatch, widthPx = 360)
                SpanText(
                    t.homeItsYourMatch,
                    modifier = Modifier
                        .sFontSize(18)
                        .fontWeight(AppTypography.Regular)
                        .sLineHeight(22)
                        .sLetterSpacing(0.36)
                        .color(AppColors.Text.Secondary)
                        .fontFamily(URBANIST, URBANIST_FB)
                )
            }
        }
    }
}

// ============================================================================
// Section 3 — Share Moments, Connect with Friends
// ============================================================================

@Composable
private fun SectionShareMoments() {
    val t = LocalAppStrings.current
    Column(
        modifier = Modifier.fillMaxWidth().sGap(40),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        // Headline — underline only under the word "Friends" (Figma 9704:473907,
        // Line 143 at x=1093 / width=111, y=37.6 inside the 42-tall title row).
        Column(
            modifier = Modifier.fillMaxWidth().sGap(2).revealOnScroll(0),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            SpanText(
                t.homeSectionMomentsTitle,
                modifier = Modifier
                    .sFontSize(32)
                    .fontWeight(AppTypography.SemiBold)
                    .sLineHeight(38)
                    .sLetterSpacing(0.64)
                    .color(AppColors.Text.Secondary)
                    .fontFamily(URBANIST, URBANIST_FB)
                    .styleModifier { property("text-align", "center") }
            )
            Box(modifier = Modifier.fillMaxWidth().sPaddingLeft(1093)) {
                Box(
                    modifier = Modifier
                        .sWidth(111)
                        .sHeight(1)
                        .backgroundColor(AppColors.Text.Secondary)
                )
            }
        }
        Row(
            modifier = Modifier.fillMaxWidth().sGap(16),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.Center,
        ) {
            MockupCard("/images/home/feed.png", t.homeAltFeedScreen, widthPx = 660, modifier = Modifier.revealOnScroll(100))
            // First arrow sits near the BOTTOM of the row (Figma 9704:474259 at y=310 of ~374-tall section).
            Box(
                modifier = Modifier.styleModifier { property("align-self", "flex-end") }
            ) {
                DiagonalArrowPill(sizePx = 64, rotateDeg = 45, modifier = Modifier.revealOnScroll(600, pop = true))
            }

            // Center pull-out: "Game Moments" pill button + feed-post mockup (Figma 9796:316229).
            Column(
                modifier = Modifier.sGap(12).flexShrink(0).revealOnScroll(250),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                PillLabel(t.homeLabelGameMoments)
                MockupCard("/images/home/feed-post.png", t.homeAltGameMomentsPost, widthPx = 220)
            }

            // Second arrow sits near the TOP of the row (Figma 9704:474300 at y=0).
            Box(
                modifier = Modifier.styleModifier { property("align-self", "flex-start") }
            ) {
                DiagonalArrowPill(sizePx = 64, rotateDeg = 90, modifier = Modifier.revealOnScroll(700, pop = true), pulseDelayMs = 400)
            }
            MockupCard("/images/home/chat.png", t.homeAltChatScreen, widthPx = 665, modifier = Modifier.revealOnScroll(400))
        }
    }
}

// ============================================================================
// Section 4 — Track Stats, Evolution Progress & Customize Card
// ============================================================================

@Composable
private fun SectionTrackStats() {
    val t = LocalAppStrings.current
    Column(
        modifier = Modifier.fillMaxWidth().sGap(40),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        // Headline — underline only under "Progress" (matches the per-word underline pattern
        // used in sections 1–3; exact Figma coordinates not available for this section).
        Column(
            modifier = Modifier.fillMaxWidth().sGap(2).revealOnScroll(0),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            SpanText(
                t.homeSectionStatsTitle,
                modifier = Modifier
                    .sFontSize(32)
                    .fontWeight(AppTypography.SemiBold)
                    .sLineHeight(38)
                    .sLetterSpacing(0.64)
                    .color(AppColors.Text.Secondary)
                    .fontFamily(URBANIST, URBANIST_FB)
                    .styleModifier { property("text-align", "center") }
            )
            Box(modifier = Modifier.fillMaxWidth().sPaddingLeft(880)) {
                Box(
                    modifier = Modifier
                        .sWidth(130)
                        .sHeight(1)
                        .backgroundColor(AppColors.Text.Secondary)
                )
            }
        }
        // Row layout — Figma 9730:151271:
        // profile (681) → arrow 64 (top) → [Player Stats pill + stats-card (~311)] → arrow 77 (top) → rate-performance (~487).
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .sGap(20)
                .styleModifier { property("transform", "translateX(${s(-50)})") },
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.Center,
        ) {
            Box(
                modifier = Modifier.styleModifier { property("align-self", "flex-end") }
            ) {
                MockupCard("/images/home/profile.png", t.homeAltProfileDashboard, widthPx = 681, modifier = Modifier.revealOnScroll(100))
            }
            // First arrow — slightly above row center. The reveal lives on the pill itself:
            // the wrapper's inline transform would override the class transition.
            Box(
                modifier = Modifier.styleModifier { property("transform", "translateY(${s(-130)})") }
            ) {
                DiagonalArrowPill(sizePx = 64, rotateDeg = 90, modifier = Modifier.revealOnScroll(600, pop = true))
            }

            Column(
                modifier = Modifier.sGap(12).flexShrink(0).revealOnScroll(250),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                PillLabel(t.homeLabelPlayerStats)
                MockupCard("/images/home/stats-card.png", t.homeAltPlayerStatsCard, widthPx = 311)
            }

            // Second arrow — same 64px size, slightly below row center, angled down-right.
            Box(
                modifier = Modifier.styleModifier { property("transform", "translateY(${s(80)})") }
            ) {
                DiagonalArrowPill(sizePx = 64, rotateDeg = 135, modifier = Modifier.revealOnScroll(700, pop = true), pulseDelayMs = 400)
            }
            Box(
                modifier = Modifier.styleModifier { property("align-self", "flex-end") }
            ) {
                MockupCard("/images/home/rate-performance.png", t.homeAltRatePerformanceModal, widthPx = 487, modifier = Modifier.revealOnScroll(400))
            }
        }
    }
}

// ============================================================================
// Headline + underline
// ============================================================================

@Composable
private fun SectionHeadline(text: String) {
    Column(
        modifier = Modifier.fillMaxWidth().sGap(12),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        SpanText(
            text,
            modifier = Modifier
                .sFontSize(32)
                .fontWeight(AppTypography.SemiBold)
                .sLineHeight(38)
                .sLetterSpacing(0.64)
                .color(AppColors.Text.Secondary)
                .fontFamily(URBANIST, URBANIST_FB)
                .styleModifier { property("text-align", "center") }
        )
        HeadlineUnderline(widthPx = 180)
    }
}

@Composable
private fun HeadlineUnderline(widthPx: Int) {
    Box(
        modifier = Modifier
            .sWidth(widthPx)
            .sHeight(1)
            .styleModifier {
                property(
                    "background",
                    "linear-gradient(90deg, rgba(60,121,241,0) 0%, rgba(60,121,241,0.6) 50%, rgba(60,121,241,0) 100%)"
                )
            }
    )
}

// ============================================================================
// Mockup card — image with subtle glow + border
// ============================================================================

@Composable
private fun MockupCard(image: String, alt: String, widthPx: Int, modifier: Modifier = Modifier) {
    Box(
        modifier = modifier
            .sWidth(widthPx)
            .flexShrink(0)
    ) {
        // The tilt transform lives on this inner element: the outer Box may carry the
        // scroll-reveal class, whose transition an inline transform would override.
        Div(
            attrs = {
                style {
                    property("box-shadow", "0 30px 80px rgba(0, 0, 0, 0.45)")
                    property("transition", "transform 180ms ease-out")
                }
                ref { el ->
                    val detach = attachTilt(el)
                    onDispose { detach() }
                }
            }
        ) {
            Img(
                src = image,
                alt = alt,
                attrs = {
                    style {
                        width(100.percent)
                        property("height", "auto")
                        property("display", "block")
                    }
                },
            )
        }
    }
}

// 3D tilt — the card leans toward the cursor (max ~5°) with a slight lift, and eases
// back flat on leave. Mouse-only by nature; touch devices simply never trigger it.
internal fun attachTilt(el: HTMLElement): () -> Unit {
    val maxDeg = 5.0
    val onMove: (Event) -> Unit = { e ->
        val me = e.unsafeCast<MouseEvent>()
        val r = el.getBoundingClientRect()
        val px = (me.clientX - r.left) / r.width - 0.5
        val py = (me.clientY - r.top) / r.height - 0.5
        el.style.transform =
            "perspective(900px) rotateX(${-py * 2 * maxDeg}deg) rotateY(${px * 2 * maxDeg}deg) scale(1.02)"
    }
    val onLeave: (Event) -> Unit = {
        el.style.transform = "perspective(900px) rotateX(0deg) rotateY(0deg) scale(1)"
    }
    el.addEventListener("mousemove", onMove)
    el.addEventListener("mouseleave", onLeave)
    return {
        el.removeEventListener("mousemove", onMove)
        el.removeEventListener("mouseleave", onLeave)
    }
}

// ============================================================================
// Chevron arrow between mockups
// ============================================================================

@Composable
private fun ChevronRight() {
    Box(
        modifier = Modifier
            .sSize(36)
            .styleModifier {
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("flex-shrink", "0")
            }
    ) {
        Box(
            modifier = Modifier
                .sSize(28)
                .styleModifier {
                    property("background-image", "url(\"data:image/svg+xml;utf8,$ChevronRightSvg\")")
                    property("background-size", "contain")
                    property("background-repeat", "no-repeat")
                    property("background-position", "center")
                }
        )
    }
}

private val ChevronRightSvg =
    "%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%233C79F1' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E" +
        "%3Cpolyline points='9 6 15 12 9 18'/%3E" +
        "%3C/svg%3E"

// Round pill with an arrow-up icon rotated by [rotateDeg]
// -45° → ↖ (top-left), +45° → ↗ (top-right) — matches Figma 9654:135447 / 9654:135601
// The icon periodically nudges along its pointing direction to lead the eye through
// the flow; [pulseDelayMs] phase-shifts the pulse so paired arrows fire in sequence.
@Composable
private fun DiagonalArrowPill(sizePx: Int, rotateDeg: Int, modifier: Modifier = Modifier, pulseDelayMs: Int = 0) {
    Box(
        modifier = modifier
            .sSize(sizePx)
            .sBorderRadius(999)
            .styleModifier {
                property("background", AppGradients.InteractiveSecondary)
                property("backdrop-filter", "blur(2px)")
                property("position", "relative")
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
            }
    ) {
        // The rotation lives in --arrow-rot rather than an inline transform: the nudge
        // keyframes must own `transform`, and an inline value would override them.
        // The style injection rides on THIS element's ref — the pill root may already
        // carry the scroll-reveal ref, and an element only gets one ref.
        Box(
            modifier = Modifier
                .sSize((sizePx * 0.5).toInt())
                .classNames("arrow-nudge")
                .attrsModifier {
                    ref { _ ->
                        injectArrowNudgeStyles()
                        onDispose { }
                    }
                }
                .styleModifier {
                    property("background-image", "url(\"data:image/svg+xml;utf8,$ArrowUpSvg\")")
                    property("background-size", "contain")
                    property("background-repeat", "no-repeat")
                    property("background-position", "center")
                    property("--arrow-rot", "${rotateDeg}deg")
                    property("animation-delay", "${pulseDelayMs}ms")
                }
        )
        // Gradient border ring — mask-composite trick so the gradient hugs the pill radius.
        Box(
            modifier = Modifier
                .fillMaxSize()
                .styleModifier {
                    property("position", "absolute")
                    property("inset", "0")
                    property("border", "0.3px solid transparent")
                    property("border-radius", "999px")
                    property("background", "${AppGradients.ButtonBorderGradientPrimary} border-box")
                    property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                    property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                    property("-webkit-mask-composite", "xor")
                    property("mask-composite", "exclude")
                    property("pointer-events", "none")
                }
        )
    }
}

// Gentle recurring nudge along the arrow's own direction (translateY runs inside the
// rotated frame). Quiet for ~2s of every 3.2s cycle so it reads as a hint, not a strobe.
internal fun injectArrowNudgeStyles() {
    val styleId = "home-arrow-nudge-styles"
    if (document.getElementById(styleId) != null) return
    val style = document.createElement("style")
    style.id = styleId
    style.textContent = """
        @keyframes arrow-nudge {
            0%, 62%, 100% { transform: rotate(var(--arrow-rot, 0deg)) translateY(0); }
            72% { transform: rotate(var(--arrow-rot, 0deg)) translateY(-7px); }
            82% { transform: rotate(var(--arrow-rot, 0deg)) translateY(2px); }
        }
        .arrow-nudge { animation: arrow-nudge 3200ms ease-in-out infinite; }
    """.trimIndent()
    document.head?.appendChild(style)
}

internal val ArrowUpSvg =
    "%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23B4CCF6' stroke-width='2.4' stroke-linecap='round' stroke-linejoin='round'%3E" +
        "%3Cline x1='12' y1='5' x2='12' y2='19'/%3E" +
        "%3Cpolyline points='5 12 12 5 19 12'/%3E" +
        "%3C/svg%3E"

// ============================================================================
// Pill label button — Figma 9796:316229 ("Game Moments"), 9730:151648 ("Player Stats"), etc.
// ============================================================================

@Composable
private fun PillLabel(text: String) {
    Box(
        modifier = Modifier
            .sBorderRadius(140)
            .sPadding(12, 24)
            .styleModifier {
                property("position", "relative")
                property(
                    "background",
                    "linear-gradient(0deg, rgba(12, 66, 255, 1) 3.12%, rgba(102, 102, 102, 0) 60.116%)"
                )
                property("backdrop-filter", "blur(3px)")
                property("-webkit-backdrop-filter", "blur(3px)")
            }
    ) {
        SpanText(
            text,
            modifier = Modifier
                .sFontSize(22)
                .fontWeight(AppTypography.Medium)
                .sLineHeight(26)
                .sLetterSpacing(0.22)
                .color(AppColors.Text.Secondary)
                .fontFamily(URBANIST, URBANIST_FB)
                .styleModifier { property("white-space", "nowrap") }
        )
        // Gradient border ring — mask-composite (per project rule on gradient borders).
        Box(
            modifier = Modifier
                .fillMaxSize()
                .styleModifier {
                    property("position", "absolute")
                    property("inset", "0")
                    property("border", "0.3px solid transparent")
                    property("border-radius", s(140))
                    property("background", "${AppGradients.ButtonBorderGradientPrimary} border-box")
                    property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                    property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                    property("-webkit-mask-composite", "xor")
                    property("mask-composite", "exclude")
                    property("pointer-events", "none")
                }
        )
    }
}

// ============================================================================
// Footer — brand+links row on top, GIANT wordmark at the very bottom
// ============================================================================

@Composable
private fun Footer() {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .sMaxWidth(1840)
            // The page layout has no bottom padding — the footer carries its own.
            .sPaddingBottom(40)
            .styleModifier { property("position", "relative") }
    ) {
        // Wordmark sits absolutely behind, shifted left of center, aligned with the brand/right row.
        Box(
            modifier = Modifier.styleModifier {
                property("position", "absolute")
                property("left", "0")
                property("right", "0")
                property("top", "0")
                property("display", "flex")
                property("justify-content", "center")
                property("transform", "translateX(${s(-100)})")
                property("pointer-events", "none")
                property("z-index", "0")
            }
        ) {
            // The faint blue fill lives in the shimmer class (background-clip: text):
            // an inline `color` would override the transparent text the effect needs.
            SpanText(
                "Arena Pro Play",
                modifier = Modifier
                    .sFontSize(144)
                    .fontWeight(AppTypography.Bold)
                    .sLineHeight(144)
                    .sLetterSpacing(2.4)
                    .fontFamily(URBANIST, URBANIST_FB)
                    .classNames("footer-wordmark-shimmer")
                    .attrsModifier {
                        ref { _ ->
                            injectFooterShimmerStyles()
                            onDispose { }
                        }
                    }
                    .styleModifier {
                        property("white-space", "nowrap")
                        property("user-select", "none")
                    }
            )
        }

        // Top row: brand pulled further left, links/socials/stores pulled further right.
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .styleModifier {
                    property("position", "relative")
                    property("z-index", "1")
                },
            verticalAlignment = Alignment.Top,
            horizontalArrangement = Arrangement.SpaceBetween,
        ) {
            Box(modifier = Modifier.sMarginLeft(-30)) { FooterBrand() }
            Box(modifier = Modifier.sMarginRight(-30)) { FooterRight() }
        }
    }
}

@Composable
private fun FooterBrand() {
    val t = LocalAppStrings.current
    Column(
        modifier = Modifier.sGap(18).sMaxWidth(276),
        horizontalAlignment = Alignment.Start,
    ) {
        Column(modifier = Modifier.sGap(12)) {
            // Full brand mark (glyph + "Arena" wordmark + "Pro Play" label), static:
            // no intro animation and no data-brand-glyph marker down here.
            BrandLogo(static = true)
            SpanText(
                t.homeFooterTagline,
                modifier = Modifier
                    .sFontSize(18)
                    .sLineHeight(23)
                    .sLetterSpacing(0.5)
                    .color(AppColors.Text.Secondary)
                    .fontFamily(URBANIST, URBANIST_FB)
                    .fontWeight(AppTypography.Regular)
            )
            // Crawlable product blurb — the home page's only body copy with
            // the core search keywords.
            SpanText(
                t.homeFooterBlurb,
                modifier = Modifier
                    .sFontSize(13)
                    .sLineHeight(19)
                    .color(AppColors.Text.Secondary)
                    .fontFamily(URBANIST, URBANIST_FB)
                    .fontWeight(AppTypography.Regular)
            )
        }
        SpanText(
            t.homeFooterCopyright,
            modifier = Modifier
                .sFontSize(12)
                .sLineHeight(12)
                .color(AppColors.Text.Secondary)
                .fontFamily(URBANIST, URBANIST_FB)
                .fontWeight(AppTypography.Regular)
        )
    }
}

@Composable
private fun FooterRight() {
    Row(modifier = Modifier.sGap(160), verticalAlignment = Alignment.Top) {
        FooterLinksColumn()
        FooterSocialAndStores()
    }
}

@Composable
private fun FooterLinksColumn() {
    val t = LocalAppStrings.current
    var showSupport by remember { mutableStateOf(false) }
    Column(modifier = Modifier.sGap(22)) {
        FooterLink(t.homeFooterAbout, "/about")
        FooterLink(t.homeFooterTerms, "/terms")
        FooterLink(t.homeFooterPrivacy, "/privacy")
        FooterLink(t.homeFooterSupports, onClick = { showSupport = true })
    }
    SupportPopup(isOpen = showSupport, onClose = { showSupport = false })
}

@Composable
private fun FooterLink(text: String, path: String? = null, onClick: (() -> Unit)? = null) {
    val ctx = rememberPageContext()
    val label: @Composable () -> Unit = {
        SpanText(
            text,
            modifier = Modifier
                .sFontSize(16)
                .color(AppColors.Text.Secondary)
                .fontFamily("Inter", "sans-serif")
                .fontWeight(AppTypography.Regular)
                .cursor(Cursor.Pointer)
                .thenIf(onClick != null) {
                    Modifier.onClick { onClick!!() }
                }
        )
    }
    if (path != null) {
        // Real <a href> so crawlers can follow internal links; navigation
        // itself stays client-side through the router.
        A(
            href = path,
            attrs = {
                style { property("text-decoration", "none") }
                onClick {
                    it.preventDefault()
                    ctx.router.navigateTo(path)
                }
            }
        ) { label() }
    } else {
        label()
    }
}

@Composable
private fun FooterSocialAndStores() {
    Column(modifier = Modifier.sGap(12)) {
        Row(modifier = Modifier.sGap(8), verticalAlignment = Alignment.CenterVertically) {
            SocialIcon(FacebookIconSvg)
            SocialIcon(InstagramIconSvg)
        }
        AppleStoreButton()
        GooglePlayButton()
    }
}

@Composable
private fun SocialIcon(svg: String) {
    Box(
        modifier = Modifier
            .sSize(44)
            .sBorderRadius(100)
            .cursor(Cursor.Pointer)
            .styleModifier {
                property("background", AppGradients.InteractiveSecondary)
                property("border", "0.5px solid rgba(255, 255, 255, 0.4)")
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
            }
    ) {
        Box(
            modifier = Modifier
                .sSize(22)
                .styleModifier {
                    property("background-image", "url(\"data:image/svg+xml;utf8,$svg\")")
                    property("background-size", "contain")
                    property("background-repeat", "no-repeat")
                    property("background-position", "center")
                }
        )
    }
}

// ============================================================================
// Store / Sign-in buttons
// ============================================================================

// Apple Store — per Figma 9654:136896 (white pill, drop-shadow, Medium eyebrow)
@Composable
private fun AppleStoreButton() {
    val t = LocalAppStrings.current
    StorePill(withShadow = true) {
        StoreIcon(AppleIconSvg)
        Column {
            SpanText(
                t.homeStoreAppleEyebrow,
                modifier = Modifier
                    .sFontSize(10)
                    .sLineHeight(12)
                    .sLetterSpacing(0.01)
                    .sMarginBottom(-2)
                    .color(AppColors.PrimaryBlack)
                    .fontFamily(URBANIST, URBANIST_FB)
                    .fontWeight(AppTypography.Medium)
            )
            SpanText(
                t.homeStoreAppleName,
                modifier = Modifier
                    .sFontSize(18)
                    .sLineHeight(22)
                    .sLetterSpacing(0.09)
                    .color(AppColors.PrimaryBlack)
                    .fontFamily(URBANIST, URBANIST_FB)
                    .fontWeight(AppTypography.SemiBold)
            )
        }
    }
}

// Google Play — per Figma 9654:136901 (white pill, no shadow, SemiBold eyebrow)
@Composable
private fun GooglePlayButton() {
    val t = LocalAppStrings.current
    StorePill(withShadow = false) {
        StoreIcon(GooglePlayIconSvg)
        Column {
            SpanText(
                t.homeStoreGoogleEyebrow,
                modifier = Modifier
                    .sFontSize(10)
                    .sLetterSpacing(0.1)
                    .sMarginBottom(-4)
                    .color(AppColors.PrimaryBlack)
                    .fontFamily(URBANIST, URBANIST_FB)
                    .fontWeight(AppTypography.SemiBold)
                    .styleModifier { property("line-height", "1.5") }
            )
            SpanText(
                t.homeStoreGoogleName,
                modifier = Modifier
                    .sFontSize(18)
                    .sLineHeight(22)
                    .sLetterSpacing(0.09)
                    .color(AppColors.PrimaryBlack)
                    .fontFamily(URBANIST, URBANIST_FB)
                    .fontWeight(AppTypography.SemiBold)
            )
        }
    }
}

@Composable
private fun StorePill(withShadow: Boolean, content: @Composable () -> Unit) {
    Row(
        modifier = Modifier
            .sWidth(180)
            .sHeight(52)
            .sBorderRadius(100)
            .backgroundColor(AppColors.PrimaryWhite)
            .sPaddingLeft(14)
            .sPaddingRight(14)
            .sGap(12)
            .cursor(Cursor.Pointer)
            .styleModifier {
                property("border", "0.5px solid rgba(255, 255, 255, 0.4)")
                if (withShadow) property("box-shadow", "0 4px 2px rgba(0, 0, 0, 0.25)")
            },
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.Center,
    ) {
        content()
    }
}

@Composable
private fun StoreIcon(svg: String) {
    Box(
        modifier = Modifier
            .sSize(32)
            .styleModifier {
                property("background-image", "url(\"data:image/svg+xml;utf8,$svg\")")
                property("background-size", "contain")
                property("background-repeat", "no-repeat")
                property("background-position", "center")
            }
    )
}

// ============================================================================
// Inline SVG icons (URL-encoded for data: URI use)
// ============================================================================

private val AppleIconSvg =
    "%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='black'%3E" +
        "%3Cpath d='M17.564 13.234c-.026-2.6 2.122-3.847 2.22-3.91-1.21-1.77-3.094-2.012-3.764-2.04-1.602-.16-3.13.945-3.945.945-.814 0-2.072-.92-3.404-.895-1.752.026-3.368 1.018-4.27 2.586-1.82 3.158-.466 7.823 1.305 10.378.864 1.252 1.895 2.659 3.246 2.61 1.302-.052 1.794-.844 3.37-.844 1.575 0 2.018.844 3.396.815 1.404-.026 2.292-1.275 3.146-2.534.99-1.456 1.402-2.866 1.428-2.94-.032-.012-2.74-1.054-2.768-4.17zM14.943 5.4c.722-.873 1.207-2.087 1.075-3.293-1.04.042-2.297.69-3.043 1.562-.668.77-1.252 2.005-1.094 3.184 1.157.088 2.34-.586 3.062-1.453z'/%3E" +
        "%3C/svg%3E"

// Google Play — official 4-colour triangle
private val GooglePlayIconSvg =
    "%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E" +
        "%3Cpath fill='%2300C2FF' d='M3.46 2.46C3.18 2.72 3 3.09 3 3.5v17c0 .41.18.78.46 1.04L13.4 12 3.46 2.46z'/%3E" +
        "%3Cpath fill='%2300C853' d='M3.46 2.46l9.94 9.54 3.41-3.41L4.53 2.1c-.41-.06-.79.1-1.07.36z'/%3E" +
        "%3Cpath fill='%23FFC107' d='M16.81 8.59L13.4 12l3.41 3.41 4.04-2.27c.69-.39.69-1.39 0-1.78l-4.04-2.77z'/%3E" +
        "%3Cpath fill='%23EA4335' d='M3.46 21.54c.28.26.66.42 1.07.36l12.28-6.49L13.4 12 3.46 21.54z'/%3E" +
        "%3C/svg%3E"

private val InstagramIconSvg =
    "%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23B4CCF6' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'%3E" +
        "%3Crect x='2' y='2' width='20' height='20' rx='5'/%3E" +
        "%3Cpath d='M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z'/%3E" +
        "%3Cline x1='17.5' y1='6.5' x2='17.51' y2='6.5'/%3E" +
        "%3C/svg%3E"

private val FacebookIconSvg =
    "%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23B4CCF6'%3E" +
        "%3Cpath d='M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.879V14.89H7.898v-2.89h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33V21.879C18.343 21.128 22 16.991 22 12z'/%3E" +
        "%3C/svg%3E"

// ============================================================================
// Ambient background — large soft blue orbs drifting slowly behind the content
// ============================================================================

// Fixed full-viewport layer at z-index 0; the showcase column sits at z-index 1 and the
// header wrapper at z-index 2, so the glow reads as depth behind everything. Radial
// gradients fading to transparent are smooth on their own — no blur filter needed
// (a viewport-sized blur would be a GPU memory hog).
@Composable
internal fun HomeAmbientOrbs() {
    Div(
        attrs = {
            style {
                property("position", "fixed")
                property("inset", "0")
                property("z-index", "0")
                property("pointer-events", "none")
                property("overflow", "hidden")
            }
            ref { _ ->
                injectAmbientOrbStyles()
                onDispose { }
            }
        }
    ) {
        Div(attrs = { classes("home-orb", "home-orb-1") })
        Div(attrs = { classes("home-orb", "home-orb-2") })
        Div(attrs = { classes("home-orb", "home-orb-3") })
    }
}

internal fun injectAmbientOrbStyles() {
    val styleId = "home-ambient-orb-styles"
    if (document.getElementById(styleId) != null) return
    val style = document.createElement("style")
    style.id = styleId
    style.textContent = """
        .home-orb { position: absolute; border-radius: 50%; }
        .home-orb-1 {
            width: 640px; height: 640px; left: -160px; top: -120px;
            background: radial-gradient(circle, rgba(0, 97, 255, 0.20) 0%, rgba(0, 97, 255, 0) 70%);
            animation: orb-drift-1 26s ease-in-out infinite;
        }
        .home-orb-2 {
            width: 820px; height: 820px; right: -240px; top: 22%;
            background: radial-gradient(circle, rgba(0, 97, 255, 0.14) 0%, rgba(0, 97, 255, 0) 70%);
            animation: orb-drift-2 34s ease-in-out infinite;
        }
        .home-orb-3 {
            width: 560px; height: 560px; left: 28%; bottom: -200px;
            background: radial-gradient(circle, rgba(60, 121, 241, 0.16) 0%, rgba(60, 121, 241, 0) 70%);
            animation: orb-drift-3 42s ease-in-out infinite;
        }
        @keyframes orb-drift-1 { 0%, 100% { transform: translate(0, 0) scale(1); } 50% { transform: translate(140px, 90px) scale(1.12); } }
        @keyframes orb-drift-2 { 0%, 100% { transform: translate(0, 0) scale(1); } 50% { transform: translate(-160px, -70px) scale(1.08); } }
        @keyframes orb-drift-3 { 0%, 100% { transform: translate(0, 0) scale(1); } 50% { transform: translate(120px, -90px) scale(1.15); } }
    """.trimIndent()
    document.head?.appendChild(style)
}

// ============================================================================
// Footer wordmark shimmer — a soft highlight sweeps across the giant text
// ============================================================================

// The text is filled by a gradient clipped to the glyphs; sliding the (oversized)
// background sweeps the highlight through. The sweep takes ~45% of the 6s cycle,
// the rest is idle so it stays ambient rather than busy.
private fun injectFooterShimmerStyles() {
    val styleId = "footer-wordmark-shimmer-styles"
    if (document.getElementById(styleId) != null) return
    val style = document.createElement("style")
    style.id = styleId
    style.textContent = """
        .footer-wordmark-shimmer {
            background-image: linear-gradient(
                110deg,
                rgba(0, 97, 255, 0.12) 42%,
                rgba(120, 170, 255, 0.38) 50%,
                rgba(0, 97, 255, 0.12) 58%
            );
            background-size: 200% 100%;
            -webkit-background-clip: text;
            background-clip: text;
            color: transparent;
            animation: wordmark-shimmer 6s ease-in-out infinite;
        }
        @keyframes wordmark-shimmer {
            0% { background-position: 130% 0; }
            45%, 100% { background-position: -30% 0; }
        }
    """.trimIndent()
    document.head?.appendChild(style)
}

// ============================================================================
// Scroll reveal — content animates in as it enters the viewport
// ============================================================================

// Injected as a raw stylesheet (same pattern as the hero segment styles): the reveal
// runs on a CSS transition triggered by a class toggle, with the per-element stagger
// carried in a custom property. `transform: none` in the revealed state keeps elements
// off dedicated GPU layers afterwards (see the fill:forwards "glow" gotcha).
internal fun injectRevealStyles() {
    val styleId = "home-reveal-styles"
    if (document.getElementById(styleId) != null) return
    val style = document.createElement("style")
    style.id = styleId
    style.textContent = """
        .reveal-rise {
            opacity: 0;
            transform: translateY(28px);
            transition: opacity 700ms ease-out, transform 700ms cubic-bezier(0.22, 1, 0.36, 1);
            transition-delay: var(--reveal-delay, 0ms);
        }
        .reveal-pop {
            opacity: 0;
            transform: scale(0.55);
            transition: opacity 550ms ease-out, transform 550ms cubic-bezier(0.34, 1.56, 0.64, 1);
            transition-delay: var(--reveal-delay, 0ms);
        }
        .reveal-rise.is-revealed, .reveal-pop.is-revealed {
            opacity: 1;
            transform: none;
        }
    """.trimIndent()
    document.head?.appendChild(style)
}

// Fades + slides the element in once it scrolls into view (pop = scale-in with
// overshoot, used for the arrow pills). One-shot: the observer lets go after firing.
// `eager` skips the observer and plays the reveal right after mount — used for the
// first two sections so the page never opens onto half-empty rows above the fold.
// Must not be combined with elements carrying their own inline `transform` — the
// inline value would override the class transition (only opacity would animate).
internal fun Modifier.revealOnScroll(delayMs: Int = 0, pop: Boolean = false, eager: Boolean = false): Modifier = this
    .classNames(if (pop) "reveal-pop" else "reveal-rise")
    .styleModifier { property("--reveal-delay", "${delayMs}ms") }
    .attrsModifier {
        ref { el ->
            injectRevealStyles()
            if (eager) {
                // Small timeout so the hidden starting state paints first — adding the
                // class in the same frame would skip the transition entirely.
                val timer = window.setTimeout({ el.classList.add("is-revealed") }, 80)
                onDispose { window.clearTimeout(timer) }
            } else {
                val onIntersect = { entries: Array<dynamic>, obs: dynamic ->
                    entries.forEach { entry ->
                        if (entry.isIntersecting == true) {
                            entry.target.classList.add("is-revealed")
                            obs.unobserve(entry.target)
                        }
                    }
                }
                val options: dynamic = js("({ threshold: 0.15, rootMargin: '0px 0px -8% 0px' })")
                val observer: dynamic = js("new IntersectionObserver(onIntersect, options)")
                observer.observe(el)
                onDispose { observer.disconnect() }
            }
        }
    }

// ============================================================================
// Helpers
// ============================================================================

@Composable
private fun Spacer(heightPx: Int) {
    Box(modifier = Modifier.sHeight(heightPx).fillMaxWidth())
}

// Gradient divider — sits in the showcase column, centered horizontally by the parent column's alignment.
@Composable
private fun GradientDivider() {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .sMaxWidth(1840)
            .sHeight(1)
            .styleModifier {
                property(
                    "background",
                    "linear-gradient(90deg, rgba(60, 121, 241, 0) 0%, rgba(60, 121, 241, 0.6) 50%, rgba(60, 121, 241, 0) 100%)"
                )
            }
    )
}
